import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Truck, Plus, Search, Fuel, Clock, Gauge, Calendar, UserCheck, Building } from 'lucide-react';
import { toast } from 'sonner';

const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
let BACKEND_URL = !ENV_BACKEND_URL || ENV_BACKEND_URL === '' ? window.location.origin : ENV_BACKEND_URL;
if (BACKEND_URL.endsWith('/api')) BACKEND_URL = BACKEND_URL.slice(0, -4);
const API = `${BACKEND_URL}/api`;

export default function MachineryPage() {
  const [logs, setLogs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLogOpen, setIsLogOpen] = useState(false);
  const [newLog, setNewLog] = useState({
      project_id: '',
      machinery_name: '',
      operator_name: '',
      hours_used: 0,
      fuel_gallons: 0,
      date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [logsRes, projRes] = await Promise.all([
        axios.get(`${API}/construction/machinery-logs`),
        axios.get(`${API}/construction/projects`)
      ]);
      setLogs(logsRes.data);
      setProjects(projRes.data);
    } catch (error) {
      toast.error('Error al cargar datos de maquinaria');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
      e.preventDefault();
      if (!newLog.project_id) {
          toast.error('Debe seleccionar una obra');
          return;
      }
      try {
          await axios.post(`${API}/construction/machinery-logs`, newLog);
          toast.success('Actividad registrada correctamente');
          setIsLogOpen(false);
          setNewLog({ project_id: '', machinery_name: '', operator_name: '', hours_used: 0, fuel_gallons: 0, date: new Date().toISOString().split('T')[0] });
          fetchData();
      } catch (error) {
          toast.error('Error al registrar actividad');
      }
  };

  const totalHours = logs.reduce((acc, curr) => acc + (curr.hours_used || 0), 0);
  const totalFuel = logs.reduce((acc, curr) => acc + (curr.fuel_gallons || 0), 0);
  const avgEfficiency = totalHours > 0 ? (totalFuel / totalHours).toFixed(1) : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Truck className="h-8 w-8 text-orange-600" />
            Control de Maquinaria
          </h1>
          <p className="text-muted-foreground">Registro de horas de uso y consumos de combustible</p>
        </div>

        <Dialog open={isLogOpen} onOpenChange={setIsLogOpen}>
          <DialogTrigger asChild>
            <Button className="w-full md:w-auto bg-orange-600 hover:bg-orange-700">
              <Plus className="mr-2 h-4 w-4" /> Registrar Actividad
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Registrar Uso de Maquinaria</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                    <label className="text-sm font-medium">Obra / Proyecto</label>
                    <Select onValueChange={(val) => setNewLog({...newLog, project_id: val})}>
                        <SelectTrigger>
                            <SelectValue placeholder="Seleccione una obra" />
                        </SelectTrigger>
                        <SelectContent>
                            {projects.map(p => (
                                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <label className="text-sm font-medium">Maquinaria (Nombre/ID)</label>
                    <Input required value={newLog.machinery_name} onChange={e => setNewLog({...newLog, machinery_name: e.target.value})} placeholder="Ej: Excavadora CAT-05" />
                </div>
                <div className="space-y-2">
                    <label className="text-sm font-medium">Operador</label>
                    <Input value={newLog.operator_name} onChange={e => setNewLog({...newLog, operator_name: e.target.value})} placeholder="Nombre del operador" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Horas de Uso</label>
                        <Input type="number" step="0.5" required value={newLog.hours_used} onChange={e => setNewLog({...newLog, hours_used: parseFloat(e.target.value)})} />
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Combustible (Lts)</label>
                        <Input type="number" step="0.1" value={newLog.fuel_gallons} onChange={e => setNewLog({...newLog, fuel_gallons: parseFloat(e.target.value)})} />
                    </div>
                </div>
                <div className="space-y-2">
                    <label className="text-sm font-medium">Fecha</label>
                    <Input type="date" value={newLog.date} onChange={e => setNewLog({...newLog, date: e.target.value})} />
                </div>
                <Button type="submit" className="w-full bg-orange-600 hover:bg-orange-700">Guardar Registro</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-orange-50 dark:bg-orange-950/20 border-orange-200">
              <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-2 bg-orange-600 rounded-lg text-white">
                      <Clock className="h-6 w-6" />
                  </div>
                  <div>
                      <p className="text-sm font-medium text-orange-800 dark:text-orange-300">Horas Totales</p>
                      <p className="text-2xl font-bold">{totalHours} hrs</p>
                  </div>
              </CardContent>
          </Card>
          
          <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200">
              <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-2 bg-blue-600 rounded-lg text-white">
                      <Fuel className="h-6 w-6" />
                  </div>
                  <div>
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-300">Combustible (Lts)</p>
                      <p className="text-2xl font-bold">{totalFuel} L</p>
                  </div>
              </CardContent>
          </Card>

          <Card className="bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200">
              <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-2 bg-emerald-600 rounded-lg text-white">
                      <Gauge className="h-6 w-6" />
                  </div>
                  <div>
                      <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">Rendimiento Prom.</p>
                      <p className="text-2xl font-bold">{avgEfficiency} L/hr</p>
                  </div>
              </CardContent>
          </Card>
      </div>

      {loading ? (
           <div className="text-center py-12 animate-pulse">Cargando registros...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {logs.map(log => (
                <Card key={log.id} className="border-l-4 border-l-orange-500 overflow-hidden shadow-sm hover:shadow-md transition-all">
                    <CardContent className="p-0">
                        <div className="p-6">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="font-bold text-lg">{log.machinery_name}</h3>
                                    <p className="text-xs text-muted-foreground flex items-center gap-1 font-mono">
                                        <Calendar className="h-3 w-3" /> {new Date(log.date).toLocaleDateString()}
                                    </p>
                                </div>
                                <Truck className="h-5 w-5 text-orange-500 opacity-30" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center gap-2 text-sm">
                                    <UserCheck className="h-4 w-4 text-muted-foreground" />
                                    <span>Op: {log.operator_name || 'Sin asignar'}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-indigo-600 font-medium">
                                    <Building className="h-4 w-4" />
                                    <span className="truncate">Obra: {log.project_id}</span>
                                </div>
                            </div>
                        </div>
                        <div className="bg-muted/30 p-4 border-t flex justify-between">
                            <div className="text-center flex-1 border-r">
                                <span className="text-[10px] uppercase font-bold text-muted-foreground block">Horas</span>
                                <span className="font-bold font-mono">{log.hours_used}</span>
                            </div>
                            <div className="text-center flex-1 border-r">
                                <span className="text-[10px] uppercase font-bold text-muted-foreground block">Combustible</span>
                                <span className="font-bold font-mono text-blue-600">{log.fuel_gallons} L</span>
                            </div>
                            <div className="text-center flex-1">
                                <span className="text-[10px] uppercase font-bold text-muted-foreground block">Eficiencia</span>
                                <span className="font-bold text-emerald-600 font-mono text-xs">{(log.fuel_gallons / (log.hours_used || 1)).toFixed(2)} L/hr</span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            ))}
            {logs.length === 0 && (
                <div className="col-span-full py-20 text-center border-2 border-dashed rounded-3xl bg-muted/5">
                    <Truck className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-10" />
                    <p className="text-muted-foreground font-medium">No hay registros de maquinaria hoy.</p>
                </div>
            )}
        </div>
      )}
    </div>
  );
}
